import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoginContext } from '../../../context/LoginContext';
import { MessageContext } from '../../../context/MessageContext';
import RequestCard from './RequestCard';
import {
  acceptConnectionRequest,
  cancelConnectionRequest,
  declineConnectionRequest,
  fetchReceivedRequests,
  fetchSentRequests,
  notifyRequestsChanged,
  refreshPremiumStatus,
} from '../../../services/connectionRequests';
import {
  clearPendingRequestId,
  getPendingRequestId,
  parseRequestId,
  savePendingRequestId,
} from '../../../utils/pendingRequest';
import './requests.css';

const EMPTY_TAB = { items: [], loading: false, loaded: false, error: '' };
const UPGRADE_REDIRECT_DELAY_MS = 1800;

const RequestsPage = () => {
  const { userId, userData, setIsPremium } = useContext(LoginContext);
  const { setMessageUserData, setMessageName, setMessageUserId, setIsMessage } = useContext(MessageContext);
  const navigate = useNavigate();
  const location = useLocation();

  const highlightId = useMemo(
    () => parseRequestId(new URLSearchParams(location.search).get('request')),
    [location.search]
  );

  const [tab, setTab] = useState('received');
  const [tabs, setTabs] = useState({ received: EMPTY_TAB, sent: EMPTY_TAB });
  const [busy, setBusy] = useState({});
  const [notice, setNotice] = useState(null);
  const inFlight = useRef(new Set());
  const redirectTimer = useRef(null);
  const scrolledTo = useRef(null);

  const load = useCallback(async (which) => {
    if (!userId) return;
    setTabs((prev) => ({ ...prev, [which]: { ...prev[which], loading: true, error: '' } }));
    try {
      const res = which === 'received'
        ? await fetchReceivedRequests(userId)
        : await fetchSentRequests(userId);
      if (res.response === 'success') {
        setTabs((prev) => ({
          ...prev,
          [which]: { items: Array.isArray(res.data) ? res.data : [], loading: false, loaded: true, error: '' },
        }));
      } else {
        setTabs((prev) => ({
          ...prev,
          [which]: { ...prev[which], loading: false, error: res.message || 'Could not load requests.' },
        }));
      }
    } catch (e) {
      setTabs((prev) => ({
        ...prev,
        [which]: { ...prev[which], loading: false, error: 'Network error. Please check your connection and retry.' },
      }));
    }
  }, [userId]);

  const reloadAll = useCallback(() => {
    load('received');
    load('sent');
    notifyRequestsChanged();
  }, [load]);

  useEffect(() => {
    load('received');
    load('sent');
    return () => clearTimeout(redirectTimer.current);
  }, [load]);

  useEffect(() => {
    if (highlightId) setTab('received');
  }, [highlightId]);

  // Bring the highlighted request into view once, and drop a stale saved id.
  useEffect(() => {
    if (!highlightId || !tabs.received.loaded) return;
    const match = tabs.received.items.find((item) => String(item.request_id) === highlightId);
    if (!match || match.status !== 'pending') {
      if (getPendingRequestId() === highlightId) clearPendingRequestId();
      if (!match && scrolledTo.current !== highlightId) {
        scrolledTo.current = highlightId;
        setNotice({ type: 'info', text: 'That request is no longer pending. It may have been withdrawn by the sender.' });
      }
      return;
    }
    if (scrolledTo.current === highlightId) return;
    scrolledTo.current = highlightId;
    const el = document.getElementById(`request-${highlightId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightId, tabs.received]);

  const runAction = async (item, action, fn) => {
    const key = String(item.request_id);
    if (inFlight.current.has(key)) return null;
    inFlight.current.add(key);
    setBusy((prev) => ({ ...prev, [key]: action }));
    setNotice(null);
    try {
      return await fn();
    } catch (e) {
      setNotice({ type: 'danger', text: 'Network error. Please check your connection and try again.' });
      return null;
    } finally {
      inFlight.current.delete(key);
      setBusy((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleAccept = (item) => runAction(item, 'accept', async () => {
    let res = await acceptConnectionRequest(userId, item.request_id);

    // A payment completed in a closed tab is only recorded once the plan status is re-read; retry once after that.
    if (res.response === 'upgrade') {
      const nowPremium = await refreshPremiumStatus(userId, userData).catch(() => false);
      if (nowPremium) {
        setIsPremium?.(true);
        res = await acceptConnectionRequest(userId, item.request_id);
      }
    }

    if (res.response === 'success') {
      if (getPendingRequestId() === String(item.request_id)) clearPendingRequestId();
      const name = res.user?.name || item.user?.name || 'this member';
      setNotice({ type: 'success', text: `Request accepted. You can now chat with ${name}.`, chatUser: res.user || item.user });
      reloadAll();
      return;
    }

    if (res.response === 'upgrade') {
      const id = parseRequestId(item.request_id);
      savePendingRequestId(id);
      setNotice({ type: 'warning', text: 'An active Premium plan is required to accept this request. Taking you to the plans page…' });
      clearTimeout(redirectTimer.current);
      redirectTimer.current = setTimeout(() => {
        navigate(id ? `/upgrade/${userId}?request=${id}` : `/upgrade/${userId}`);
      }, UPGRADE_REDIRECT_DELAY_MS);
      return;
    }

    if (res.code === 'withdrawn' && getPendingRequestId() === String(item.request_id)) {
      clearPendingRequestId();
    }
    setNotice({ type: 'danger', text: res.message || 'Could not accept the request.' });
    reloadAll();
  });

  const handleDecline = (item) => {
    if (!window.confirm('Decline this request?')) return;
    runAction(item, 'decline', async () => {
      const res = await declineConnectionRequest(userId, item.request_id);
      if (getPendingRequestId() === String(item.request_id)) clearPendingRequestId();
      setNotice(res.response === 'success'
        ? { type: 'secondary', text: 'Request declined.' }
        : { type: 'danger', text: res.message || 'Could not decline the request.' });
      reloadAll();
    });
  };

  const handleCancel = (item) => {
    if (!window.confirm('Withdraw this request?')) return;
    runAction(item, 'cancel', async () => {
      const res = await cancelConnectionRequest(userId, item.request_id);
      setNotice(res.response === 'success'
        ? { type: 'secondary', text: 'Request withdrawn.' }
        : { type: 'danger', text: res.message || 'Could not cancel the request.' });
      reloadAll();
    });
  };

  const startChat = (user) => {
    if (!user?.user_id) return;
    setMessageUserData(user);
    setMessageName(user.name);
    setMessageUserId(String(user.user_id));
    setIsMessage(true);
    navigate(`/chat/${user.user_id}`);
  };

  const current = tabs[tab];
  const pendingReceived = tabs.received.items.filter((i) => i.status === 'pending').length;

  const renderBody = () => {
    if (current.loading && !current.loaded) {
      return [0, 1, 2].map((n) => (
        <div className="card request-card mb-3" key={n} aria-hidden="true">
          <div className="card-body d-flex gap-3 placeholder-glow">
            <span className="placeholder rounded-circle" style={{ width: 64, height: 64, flex: '0 0 64px' }} />
            <div className="flex-grow-1">
              <span className="placeholder col-5 mb-2 d-block" />
              <span className="placeholder col-8 mb-2 d-block" />
              <span className="placeholder col-3 d-block" />
            </div>
          </div>
        </div>
      ));
    }

    if (current.error && !current.loaded) {
      return (
        <div className="alert alert-danger d-flex justify-content-between align-items-center flex-wrap gap-2" role="alert">
          <span>{current.error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => load(tab)}>Retry</button>
        </div>
      );
    }

    if (current.items.length === 0) {
      return (
        <div className="requests-empty">
          {tab === 'received'
            ? 'No requests yet. When a member sends you a connection request, it will appear here.'
            : 'You have not sent any requests yet. Open a profile and tap “Send Request”.'}
        </div>
      );
    }

    return (
      <>
        {current.error && (
          <div className="alert alert-warning d-flex justify-content-between align-items-center flex-wrap gap-2 py-2" role="alert">
            <span>{current.error}</span>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => load(tab)}>Retry</button>
          </div>
        )}
        {current.items.map((item) => (
          <RequestCard
            key={item.request_id}
            item={item}
            highlighted={tab === 'received' && highlightId === String(item.request_id)}
            busyAction={busy[String(item.request_id)]}
            onAccept={handleAccept}
            onDecline={handleDecline}
            onCancel={handleCancel}
            onStartChat={startChat}
          />
        ))}
      </>
    );
  };

  return (
    <div className="container requests-page py-4 py-md-5 px-3">
      <h4 className="requests-page__title mb-1">Connection Requests</h4>
      <p className="text-muted mb-3">Accept a request to start chatting. Declining is always free.</p>

      <ul className="nav nav-pills requests-tabs mb-3" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'received'}
            className={`nav-link ${tab === 'received' ? 'active' : ''}`}
            onClick={() => setTab('received')}
          >
            Received
            {pendingReceived > 0 && <span className="badge rounded-pill bg-light text-danger">{pendingReceived}</span>}
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'sent'}
            className={`nav-link ${tab === 'sent' ? 'active' : ''}`}
            onClick={() => setTab('sent')}
          >
            Sent
          </button>
        </li>
      </ul>

      {notice && (
        <div className={`alert alert-${notice.type} d-flex justify-content-between align-items-center flex-wrap gap-2`} role="status">
          <span>{notice.text}</span>
          {notice.chatUser?.user_id && (
            <button type="button" className="btn btn-sm btn-danger" onClick={() => startChat(notice.chatUser)}>
              Start chat
            </button>
          )}
        </div>
      )}

      {renderBody()}
    </div>
  );
};

export default RequestsPage;
